export const minDate = Date.UTC(1900, 0, 1);
export const maxDate = Date.UTC(3000, 11, 31);

export function parseUserDate(value: string): Date | undefined {
  if (value.trim() === '') {
    return undefined;
  }

  const regex =
    /^\s*(\d{1,2})\s*[.,-\/]\s*(\d{1,2})(?:\s*[.,-\/]\s*(\d{2,4}))?\s*$/;
  const match = regex.exec(value);
  if (!match) {
    return undefined;
  }
  const [, month, day, year] = match;
  const date = new Date();
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  if (year) {
    let yearn = parseInt(year, 10);
    if (yearn <= date.getFullYear() + 2 - 2000) {
      yearn += 2000;
    } else if (yearn < 100) {
      yearn += 1900;
    } else if (yearn < 1000) {
      yearn *= 10;
    }
    date.setFullYear(yearn);
  }
  if (month) {
    date.setMonth(parseInt(month, 10) - 1);
  }
  if (day) {
    date.setDate(parseInt(day, 10));
  }
  return date;
}

const userTimeRegex =
  /^(?<hour>0?[1-9]|1[0-2])(?:\s*[:.]\s*(?<minute>[0-5]\d))?\s*(?<period>[ap])\.?(?:\s*m\.?)?$/i;

export function parseUserTime(value: string): string | null {
  value = value.trim();

  if (value === '') {
    return null;
  }

  const match = userTimeRegex.exec(value);

  if (!match?.groups) {
    return null;
  }

  let hour = Number(match.groups.hour);
  const minute = match.groups.minute ? Number(match.groups.minute) : 0;
  const period = match.groups.period.toLowerCase();

  if (period === 'a') {
    if (hour === 12) {
      hour = 0;
    }
  } else {
    if (hour !== 12) {
      hour += 12;
    }
  }

  return `${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}`;
}

export function getAge(
  dob: Date,
  today = new Date()
): { years: number; months: number } {
  let years = today.getUTCFullYear() - dob.getUTCFullYear();
  let months = today.getUTCMonth() - dob.getUTCMonth();
  if (months < 0 || (months === 0 && today.getUTCDate() < dob.getUTCDate())) {
    years--;
  }
  if (months < 0) {
    months += 12;
  }
  return { years, months };
}

export function addDays(date: Date | number, days: number): Date {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export const relativeDatePattern = /^(\d{1,4}) days? (ago|from now)$/;

export function isRelativeDate(value: string): boolean {
  return (
    value === 'Today' ||
    value === 'Yesterday' ||
    value === 'Tomorrow' ||
    relativeDatePattern.test(value)
  );
}

export function parseRelativeDate(
  today: number,
  value: string
): number | undefined {
  if (value === 'Today') {
    return today;
  }
  if (value === 'Yesterday') {
    return addDays(today, -1).getTime();
  }
  if (value === 'Tomorrow') {
    return addDays(today, 1).getTime();
  }
  const [, rawN, rawDir] = relativeDatePattern.exec(value) || [];
  const n = parseInt(rawN, 10);
  if (!isFinite(n) || n < 1 || n > 9999) {
    return undefined;
  }
  const dir = rawDir === 'ago' ? -1 : 1;
  return addDays(today, dir * n).getTime();
}

/*
export function parseRelativeDateOrDate(value: string): string {
  if (isRelativeDate(value)) {
    return value;
  }
  const localDate = parseUserDate(value);
  if (!localDate) {
    throw new Error(
      `Expected relative date or user date. Received "${value}".`
    );
  }
  return localDate.toISOString().substr(0, 10);
}
*/

export const userDateFormat = new Intl.DateTimeFormat('en-us', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
});

export const userUTCDateFormat = new Intl.DateTimeFormat('en-us', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  timeZone: 'UTC'
});

export const weekdayFormat = new Intl.DateTimeFormat('en-us', {
  weekday: 'long'
});

/**
 * Converts a time string in 24 hour format (HH:mm) to 12 hour format (h:mm AM/PM).
 *
 * @param value A string in 24 hour time format (HH:mm)
 * @returns An object with the formatted value and a boolean indicating success
 *          { value: string, formatted: boolean }
 */
export function tryFormat24HourTime(value: string) {
  // Validate basic pattern
  if (!/^\d\d:\d\d$/.test(value)) {
    return { value, success: false };
  }

  let [hours, minutes] = value.split(':').map(Number);

  // Validate numeric ranges (00-23 for hours, 00-59 for minutes)
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return { value, success: false };
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return {
    value: `${hours}:${String(minutes).padStart(2, '0')} ${period}`,
    success: true
  };
}

/**
 * Converts a time string in 24 hour format (HH:mm) to 12 hour format (h:mm AM/PM).
 */
export function format24HourTime(time: string) {
  let [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function dateDiffDays(start: Date, end: Date): number {
  return Math.floor((end.getTime() - start.getTime()) / 86400000);
}

export function addUTCMonthsSqlStyle(
  date: Date,
  monthsToAdd: number
): Date | null {
  function positiveModulo(value: number, divisor: number): number {
    return ((value % divisor) + divisor) % divisor;
  }

  function makeUTCDate(
    year: number,
    month: number,
    day: number,
    hours: number,
    minutes: number,
    seconds: number,
    milliseconds: number
  ): Date {
    // Date.UTC treats years 0-99 as 1900-1999.
    // Adding 400 preserves leap-year behavior because the Gregorian calendar repeats every 400 years.
    const safeYear = year >= 0 && year <= 99 ? year + 400 : year;

    const result = new Date(
      Date.UTC(safeYear, month, day, hours, minutes, seconds, milliseconds)
    );

    if (safeYear !== year) {
      result.setUTCFullYear(year);
    }

    return result;
  }

  function daysInUTCMonth(year: number, month: number): number {
    return makeUTCDate(year, month + 1, 0, 0, 0, 0, 0).getUTCDate();
  }

  const originalYear = date.getUTCFullYear();
  const originalMonth = date.getUTCMonth();
  const originalDay = date.getUTCDate();

  const absoluteMonth = originalYear * 12 + originalMonth + monthsToAdd;

  const targetYear = Math.floor(absoluteMonth / 12);
  const targetMonth = positiveModulo(absoluteMonth, 12);

  // SQL Server date range is 0001-01-01 through 9999-12-31.
  // Adjust this if your expression language intentionally allows JS's wider Date range.
  if (targetYear < 1 || targetYear > 9999) {
    return null;
  }

  const targetDay = Math.min(
    originalDay,
    daysInUTCMonth(targetYear, targetMonth)
  );

  const result = makeUTCDate(
    targetYear,
    targetMonth,
    targetDay,
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );

  return Number.isFinite(result.getTime()) ? result : null;
}
